import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '../../../components/ui';
import { useKnowledgeBase } from '../../../contexts/KnowledgeBaseContext';
import type { KnownErrorContent } from '../../../types/knowledgeBase';
import './KnownErrorView.css';

interface KnownErrorViewProps {
  content: KnownErrorContent;
}

export function KnownErrorView({ content }: KnownErrorViewProps) {
  const navigate = useNavigate();
  const { getRelatedArticles } = useKnowledgeBase();

  const relatedArticles = getRelatedArticles(content.relatedArticleIds);

  const handleArticleClick = useCallback(
    (articleId: string) => {
      navigate(`/knowledge-base/article/${articleId}`);
    },
    [navigate]
  );

  return (
    <Box className="known-error-view">
      {/* Affected Systems - First/Top */}
      <Box className="known-error-view__section" component="section" aria-labelledby="affected-systems-title">
        <Box className="known-error-view__section-header">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="known-error-view__icon known-error-view__icon--systems"
            aria-hidden="true"
          >
            <rect x="2" y="4" width="16" height="10" rx="2" stroke="currentColor" strokeWidth="2" />
            <path d="M6 17h8M10 14v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <Typography id="affected-systems-title" variant="h6" className="known-error-view__section-title">
            Affected Systems
          </Typography>
        </Box>
        <Box className="known-error-view__systems" role="list" aria-label="Affected systems">
          {content.affectedSystems.map((system) => (
            <span key={system} className="known-error-view__system" role="listitem">
              {system}
            </span>
          ))}
        </Box>
      </Box>

      {/* Symptoms */}
      <Box className="known-error-view__section" component="section" aria-labelledby="symptoms-title">
        <Box className="known-error-view__section-header">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="known-error-view__icon known-error-view__icon--symptoms"
            aria-hidden="true"
          >
            <path
              d="M10 4v6M10 14h.01"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="2" />
          </svg>
          <Typography id="symptoms-title" variant="h6" className="known-error-view__section-title">
            Symptoms
          </Typography>
        </Box>
        <Typography variant="body1" className="known-error-view__section-content">
          {content.symptoms}
        </Typography>
      </Box>

      {/* Workaround / Fix - Main Solution */}
      <Box
        className="known-error-view__section known-error-view__section--workaround"
        component="section"
        aria-labelledby="workaround-title"
      >
        <Box className="known-error-view__section-header">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="known-error-view__icon known-error-view__icon--workaround"
            aria-hidden="true"
          >
            <path
              d="M5 10l3 3 7-7"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="2" />
          </svg>
          <Typography id="workaround-title" variant="h6" className="known-error-view__section-title">
            Workaround / Fix
          </Typography>
        </Box>
        <Typography variant="body1" className="known-error-view__section-content">
          {content.workaround}
        </Typography>
      </Box>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <Box className="known-error-view__section" component="section" aria-labelledby="related-articles-title">
          <Box className="known-error-view__section-header">
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="known-error-view__icon"
              aria-hidden="true"
            >
              <path
                d="M8 12h4M4 8h12M6 4h8a2 2 0 012 2v8a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            <Typography id="related-articles-title" variant="h6" className="known-error-view__section-title">
              Related Articles
            </Typography>
          </Box>
          <Box className="known-error-view__related" role="list" aria-label="Related articles">
            {relatedArticles.map((article) => (
              <button
                key={article.id}
                className="known-error-view__related-article"
                onClick={() => handleArticleClick(article.id)}
                type="button"
                role="listitem"
                aria-label={`View article: ${article.title}`}
              >
                <span className="known-error-view__related-title">{article.title}</span>
                <span className="known-error-view__related-category">{article.category}</span>
              </button>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}
